import { defineStore } from 'pinia';
import i18n from '@/plugins/vue-i18n';
import { analyticsService } from '@/plugins/post-hog';

import {
  addUser,
  createSite,
  downloadClaimSummary,
  exportSiteCashflow,
  getContractTypes,
  getDraws,
  getSite,
  getSiteCashflow,
  getSiteCashflowGraph,
  getSiteUsers,
  removeUser,
  updateCashflowOverheads,
  updateDraw,
  updateSite,
  updateSiteClaimBannerDisplay,
} from '@/api/site.service';
import { getProjectSites } from '@/api/project.service';

import {
  ContractType,
  CreateSiteBody,
  Draw,
  Site,
  SiteParams,
  SiteUser,
  SiteUserResponse,
  SiteUserRole,
  UpdateCashflow,
  UpdateSiteBody,
} from '@/types/api/sites.types';
import { Role, SiteRoles } from '@/types/api/roles.types';
import { Pagination } from '@/types/api/response.types';
import {
  Cashflow,
  CashflowGraph,
  CashflowMonth,
  CashflowParams,
  Project,
} from '@/types/api/project.types';

import { useProjectStore } from '@/stores/project.store';
import { useToastStore } from '@/stores/utils/toast.store';

import { isSuccess } from '@/common/utils/http.utils';
import { exportFile } from '@/common/utils/general.utils';
import { getFutureMonthIndexes } from '@/common/utils/site.utils';
import { convertToISOWithTimezone } from '@/common/utils/date.utils';
import { showRoleAddedToast, showUserInvitedToast } from '@/common/utils/user-role-toast.utils';
import { getRoleDisplayName, selectedOrgId } from '@/common/utils/role.utils';

const { t } = i18n.global;

// Context: the BOQ page re-reads the draws after a date save while Bank Draws re-reads them after its
// own, so only the newest read may write them; an older answer landing last would put back a
// schedule from before the user's change.
let siteDrawsRequest = 0;

const initialState = () => ({
  site: {} as Site,
  siteUsersByRoles: [] as SiteUserResponse[],
  startDate: '',
  paginatedSites: [] as Site[],
  draws: [] as Draw[],
  draw: {} as Draw,
  siteCashflow: {} as Cashflow,
  siteCashflowGraphData: {} as CashflowGraph,
  overheadUpdateResponse: {} as CashflowMonth,
  siteCashflowFutureMonthIndexes: [] as number[],
  exportedCashflow: null as Blob | null,
  downloadedClaimSummary: null as Blob | null,
  siteAddressLine1: '',
  siteAddressCountry: '',
  siteAddressCountryCode: '',
  deliveryAddressLine1: '',
  deliveryAddressCountry: '',
  deliveryAddressCountryCode: '',
  contractTypes: [] as ContractType[],

  pagination: {} as Pagination,

  siteParams: {
    pageNo: 1,
    pageSize: 10,
    orderByDesc: 'StartDate',
  } as SiteParams,
});

export const useSiteStore = defineStore('site', {
  state: initialState,

  actions: {
    // Replaces Pinia's own, which leaves the token alone: a draws read in flight across an
    // organisation switch would otherwise write the old organisation's schedule.
    $reset() {
      siteDrawsRequest++;
      this.$patch((state) => Object.assign(state, initialState()));
    },

    async createSite(data: CreateSiteBody) {
      const projectStore = useProjectStore();
      const toastStore = useToastStore();
      const response = await createSite(data);
      this.site = response.data.data;

      if (isSuccess(response.status)) {
        toastStore.showToast('success', t('sites.createSiteSuccess.title'));
        projectStore.newlyCreatedProject = {} as Project;
        this.siteAddressLine1 = '';
        this.deliveryAddressLine1 = '';

        analyticsService.capture('site_created', {
          org_id: selectedOrgId.value!,
          site_id: this.site.id,
          site_name: this.site.name,
        });
      }
    },

    /**
     * Mirrors the current `site` into `paginatedSites`. The sites table renders its rows
     * (including the member avatars) from the list, not from `site`, so any in-place
     * mutation of `site` — member added/removed, role changed — has to be pushed back here
     * or the row stays stale until the next fetch.
     */
    syncSiteInList() {
      if (!this.site?.id) return;

      this.paginatedSites = this.paginatedSites.map((site) =>
        site.id === this.site.id ? this.site : site,
      );
    },

    async getSite(id: string) {
      const response = await getSite(id);
      this.site = response.data.data;

      this.syncSiteInList();
    },

    async getSiteUsers(
      id: string,
      data: {
        roles: string[];
        includesPending?: boolean;
      },
    ) {
      const response = await getSiteUsers(id, data);
      this.siteUsersByRoles = response.data.data;
    },

    async updateSite(id: string, data: UpdateSiteBody) {
      const toastStore = useToastStore();

      const response = await updateSite(id, data);
      this.site = response.data.data;
      this.syncSiteInList();

      if (isSuccess(response.status)) {
        toastStore.showToast('success', t('sites.updateSiteSuccess.title'));
      }
    },

    async addUser(id: string, user: SiteUser, options: { skipToast?: boolean } = {}) {
      // Whether this email already holds another role on this site, to pick "Role added" vs
      // "User Invited" for the toast title below.
      const hasOtherRoles = (this.site.siteUserRoles ?? []).some(
        (existingUserRole) =>
          existingUserRole.email === user.email &&
          existingUserRole.siteRole?.code !== SiteRoles.SITE_MEMBER,
      );

      const response = await addUser(id, [user]);

      const newUserRole = response.data.data.find(
        (siteUserRole) =>
          siteUserRole.email.toLowerCase() === user.email.toLowerCase() &&
          siteUserRole.siteRole?.code === user.siteRoleCode,
      ) as SiteUserRole;

      if (!newUserRole) {
        console.warn('addUser: site user role not found', {
          user,
          response: response.data.data,
        });
        return;
      }

      this.site = {
        ...this.site,
        siteUserRoles: [...(this.site.siteUserRoles ?? []).filter(Boolean), newUserRole],
      };

      this.syncSiteInList();

      if (isSuccess(response.status) && !options.skipToast) {
        const role = getRoleDisplayName(user.siteRoleCode as SiteRoles);
        if (hasOtherRoles) {
          showRoleAddedToast(user.email, role);
        } else {
          showUserInvitedToast('site', user.email, role);
        }
      }
    },

    async removeUser(id: string, options: { skipToast?: boolean } = {}): Promise<boolean> {
      const toastStore = useToastStore();

      const response = await removeUser(id);
      this.site = {
        ...this.site,
        siteUserRoles: (this.site.siteUserRoles ?? []).filter((user) => user.id !== id),
      };

      this.syncSiteInList();

      const success = isSuccess(response.status);
      if (success && !options.skipToast) {
        toastStore.showToast('success', t('sites.users.removed.title'));
      }
      return success;
    },

    /**
     * Patches a single site-user-role entry's role in place (e.g. after an atomic role
     * update) — no refetch needed. Takes the new `Role` directly rather than trusting the
     * API response's shape for the rest of the row (email/name), which isn't guaranteed
     * to match `SiteUserRole` — only the role itself changed.
     */
    updateUserRoleCode(id: string, newRole: Role) {
      this.site = {
        ...this.site,
        siteUserRoles: (this.site.siteUserRoles ?? []).map((user) =>
          user.id === id ? { ...user, siteRole: newRole } : user,
        ),
      };

      this.syncSiteInList();
    },

    async getSites(params: SiteParams) {
      const response = await getProjectSites({ ...this.siteParams, ...params });

      const { pageCount, totalCount, pageNo, pageSize } = response.data.meta.pagination;
      this.pagination = { pageCount, totalCount, pageNo, pageSize };

      this.siteParams = { ...this.siteParams, ...params };

      this.paginatedSites = response.data.data;
    },

    async getSiteDraws(id: string) {
      const request = ++siteDrawsRequest;
      const response = await getDraws(id);
      if (request !== siteDrawsRequest) return;

      this.draws = response.data.data;
    },

    async updateSiteDraws(id: string, data: Partial<Draw>[]) {
      const response = await updateDraw(id, data);

      if (isSuccess(response.status) && Array.isArray(response.data.data)) {
        const updatedDraw = response.data.data[0];

        if (!updatedDraw?.id) {
          console.warn('Draw object missing or has no ID:', updatedDraw);
          return;
        }

        this.draw = updatedDraw;

        const index = this.draws.findIndex(
          (draw: Draw) => String(draw.id) === String(this.draw.id),
        );

        if (index === -1) {
          console.warn('Draw not found in this.draws:', this.draw.id);
        } else {
          this.draws.splice(index, 1, this.draw);
        }
      } else {
        console.warn('Unexpected response structure:', response.data);
      }
    },

    async getSiteCashflow(id: string, params: CashflowParams) {
      const response = await getSiteCashflow(id, params);
      this.siteCashflow = response.data.data;
    },

    async updateCashflowOverheads(id: string, data: UpdateCashflow) {
      const response = await updateCashflowOverheads(id, data);

      if (isSuccess(response.status)) {
        this.overheadUpdateResponse = response.data.data;
        new Date(this.overheadUpdateResponse.month).setDate(1);
        new Date(this.overheadUpdateResponse.month).setHours(0, 0, 0, 0);

        const monthToUpdate = this.siteCashflow.months.find((monthObject: CashflowMonth) => {
          return monthObject.month === data.month;
        });

        if (monthToUpdate) {
          monthToUpdate.cashIn = this.overheadUpdateResponse.cashIn;
          monthToUpdate.cashOut = this.overheadUpdateResponse.cashOut;
          monthToUpdate.overheads = this.overheadUpdateResponse.overheads;
          monthToUpdate.vatLiability = this.overheadUpdateResponse.vatLiability;
          monthToUpdate.net = this.overheadUpdateResponse.net;
        }
      }
    },

    async getSiteCashflowGraph(id: string, params: CashflowParams) {
      const response = await getSiteCashflowGraph(id, params);
      this.siteCashflowGraphData = response.data.data;
      this.siteCashflowFutureMonthIndexes = getFutureMonthIndexes(
        this.siteCashflowGraphData.cumulativeNet,
      );
    },

    async updateSiteClaimBannerDisplay(id: string) {
      const response = await updateSiteClaimBannerDisplay(id);
      this.site = response.data.data;
    },

    async exportSiteCashflow(id: string, siteName: string) {
      const toastStore = useToastStore();

      try {
        const response = await exportSiteCashflow(id);
        const contentType =
          (response.headers['content-type'] as string) ?? 'application/octet-stream';

        this.exportedCashflow = new Blob([response.data], {
          type: contentType,
        });

        exportFile(this.exportedCashflow, contentType, `${siteName} - Cashflow`);

        toastStore.showToast(
          'success',
          t('projects.cashflow.exportCashflow.exportSuccess.title'),
          t('projects.cashflow.exportCashflow.exportSuccess.content', { name: siteName }),
        );
      } catch (error) {
        console.error('Error downloading the file:', error);
      }
    },

    async downloadClaimSummary(id: string, siteName: string) {
      const toastStore = useToastStore();

      try {
        const response = await downloadClaimSummary(id);
        const contentType =
          (response.headers['content-type'] as string) ?? 'application/octet-stream';

        this.downloadedClaimSummary = new Blob([response.data], {
          type: contentType,
        });

        exportFile(this.downloadedClaimSummary, contentType, `${siteName} - ClaimSummary`);

        toastStore.showToast(
          'success',
          t('sites.claims.claimTable.downloadSuccessToast.title', {
            claim: t('sites.claims.claimSheet'),
          }),
          t('sites.claims.claimTable.downloadSuccessToast.content', {
            claim: t('sites.claims.claimSheet'),
          }),
        );
      } catch (error) {
        console.error('Error downloading the file:', error);
      }
    },

    async getContractTypes() {
      const response = await getContractTypes();
      this.contractTypes = response.data.data;
    },

    setStartDate(date: string) {
      this.startDate = convertToISOWithTimezone(date);
    },
  },
});
